// @ts-nocheck
import React, { useEffect, useCallback, useRef } from 'react';
import { Grid, Box, CircularProgress } from '@mui/material';
import StatCard from './StatCard';
import TokensByProjectPie from './TokensByProjectPie';
import QueriesOverTimeChart from './QueriesOverTimeChart';
import AnswerQualityChart from './AnswerQualityChart';
import UsageTrendsChart from './UsageTrendsChart';
import UserAccessTrendChart from './UserAccessTrendChart';
import FeedbackOverviewCard from './FeedbackOverviewCard';
import PIIWarningCard from './PIIWarningCard';
import TopUsersTable from './TopUsersTable';
import InactiveUsersTable from './InactiveUsersTable';
import { useFetchDashboardStats, useFetchUsageTrends } from '../../hooks/KnowledgeAI/useDashboardApi';

interface GeneralOverviewProps {
  onUserClick?: (username: string) => void;
  onFeedbackClick?: (feedbackType: string) => void;
  onPiiClick?: () => void;
  projects?: any;
  projectsLoading?: boolean;
}

export default function GeneralOverview({
  onUserClick,
  onFeedbackClick,
  onPiiClick,
  projects,
  projectsLoading,
}: GeneralOverviewProps) {
  const { data: apiData, loading: statsLoading, fetchDashboardStats } = useFetchDashboardStats();
  const { data: usageTrendsApiData, fetchUsageTrends } = useFetchUsageTrends();
  // Separate instance for Queries Over Time date filter — keeps Usage Trends independent
  const { data: queriesViaTrendsData, fetchUsageTrends: fetchQueriesViaTrends } = useFetchUsageTrends();
  // Only use the dedicated queries API data after the user has changed dates
  const queriesDateChanged = useRef(false);

  // Fetch on mount — no initial call for queriesOverTime; use stats data until user changes dates
  useEffect(() => {
    fetchDashboardStats();
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const today = now.toISOString().slice(0, 10);
    fetchUsageTrends(startOfMonth, today);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUsageTrendsDateChange = useCallback(
    (startDate: string, endDate: string) => {
      fetchUsageTrends(startDate, endDate);
    },
    [fetchUsageTrends]
  );

  const handleQueriesDateChange = useCallback(
    (startDate: string, endDate: string) => {
      queriesDateChanged.current = true;
      fetchQueriesViaTrends(startDate, endDate);
    },
    [fetchQueriesViaTrends]
  );

  // Resolve data from API only — no mock fallbacks
  const stats = apiData?.stats || {};
  const rawFeedback = apiData?.feedbackSummary || {};
  const feedbackData = {
    good: rawFeedback.good || 0,
    partiallyCorrect: rawFeedback.partiallyCorrect || rawFeedback['Partially Wrong'] || 0,
    fullWrong: rawFeedback.fullWrong || rawFeedback['Fully Wrong'] || 0,
    noFeedback: rawFeedback.noFeedback || rawFeedback['No Feedback'] || 0,
    other: rawFeedback.other || rawFeedback['Other'] || 0,
  };
  const tokenUsage = apiData?.tokenUsageByProject || [];

  // Initially use stats API data; after user changes dates, switch to usage trends API (date+queries only)
  const queriesOverTime = (() => {
    if (!queriesDateChanged.current) return apiData?.queriesOverTime ?? [];
    if (Array.isArray(queriesViaTrendsData?.usageTrends)) {
      return queriesViaTrendsData.usageTrends.map(({ date, queries }: { date: string; queries: number }) => ({
        date,
        queries,
      }));
    }
    return apiData?.queriesOverTime ?? [];
  })();
  const usageTrendsData = usageTrendsApiData || apiData?.usageTrends || [];
  const topUsers = apiData?.topUsers || [];
  const accessTrend = apiData?.userAccessTrend || [];
  const inactiveUsers = apiData?.inactiveUsers || [];
  const piiSummary = {
    piiQueries: apiData?.piiSummary?.piiQueries ?? 0,
    totalQueries: stats.totalConversations || 0,
  };

  if (statsLoading && !apiData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Summary Stat Cards */}
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Projects"
          value={!projectsLoading && Array.isArray(projects) ? projects.length : stats.totalProjects}
          icon="mdi:folder-multiple"
          color="primary"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Total Users" value={stats.totalUsers} icon="mdi:account-group" color="info" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Total Conversations" value={stats.totalConversations} icon="mdi:forum" color="warning" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Total Cost" value={stats.totalCost} icon="mdi:currency-usd" color="error" prefix="$" />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Total Tokens Used" value={stats.totalTokensUsed} icon="mdi:counter" color="secondary" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard title="Documents Uploaded" value={stats.totalDocuments} icon="mdi:file-upload" color="success" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Users Gave Feedback"
          value={
            (feedbackData.good || 0) +
            (feedbackData.partiallyCorrect || 0) +
            (feedbackData.fullWrong || 0) +
            (feedbackData.other || 0)
          }
          icon="mdi:message-star"
          color="warning"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Avg Queries/Day"
          value={Math.round(stats.totalConversations / 180)}
          icon="mdi:chart-timeline-variant"
          color="success"
          formatNumber={false}
        />
      </Grid>

      {/* Token Usage by Project - Pie Chart */}
      <Grid item xs={12} md={6} lg={4}>
        <TokensByProjectPie
          title="Token Usage by Project"
          subheader="Distribution across projects"
          chartData={tokenUsage.map((t) => ({ label: t.projectName, value: t.tokens }))}
        />
      </Grid>

      {/* Answer Quality */}
      <Grid item xs={12} md={6} lg={4}>
        <AnswerQualityChart
          title="Answer Quality"
          subheader="Based on user feedback"
          data={feedbackData}
          onFeedbackClick={onFeedbackClick}
        />
      </Grid>

      {/* Feedback Overview */}
      <Grid item xs={12} md={6} lg={4}>
        <FeedbackOverviewCard title="Feedback Breakdown" data={feedbackData} onFeedbackClick={onFeedbackClick} />
      </Grid>

      {/* Queries Over Time */}
      <Grid item xs={12}>
        <QueriesOverTimeChart
          title="Queries Over Time"
          subheader="Total queries trend"
          data={queriesOverTime}
          onDateChange={handleQueriesDateChange}
        />
      </Grid>

      {/* Usage Trend - Users with Access vs Active Users */}
      <Grid item xs={12} md={6} lg={8}>
        <UserAccessTrendChart
          title="Usage Trend"
          subheader="Users with access vs. actively using the system"
          data={accessTrend}
        />
      </Grid>

      {/* PII Warning */}
      <Grid item xs={12} md={6} lg={4}>
        <PIIWarningCard
          title="PII Warning"
          subheader="Queries containing Personally Identifiable Information"
          data={piiSummary}
          onPiiClick={onPiiClick}
        />
      </Grid>

      {/* Usage Trends - Tokens & Cost over time */}
      <Grid item xs={12}>
        <UsageTrendsChart
          title="Usage Trends"
          subheader="Token consumption & cost over time"
          data={usageTrendsData}
          onDateChange={handleUsageTrendsDateChange}
        />
      </Grid>

      {/* Top Users Table */}
      <Grid item xs={12}>
        <TopUsersTable
          title="Top Users"
          subheader="Click on a user to view detailed activity"
          users={topUsers}
          onUserClick={onUserClick}
        />
      </Grid>

      {/* Inactive Users Table */}
      <Grid item xs={12}>
        <InactiveUsersTable title="Inactive Users" users={inactiveUsers} />
      </Grid>
    </Grid>
  );
}
